// ============================================
// Loading & Empty State Components
// ============================================

import { Loader2, Inbox } from 'lucide-react';
import { cn } from '../lib/utils';

// Spinner
interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const spinnerSizes = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-10 h-10',
};

export function Spinner({ size = 'md', className }: SpinnerProps) {
  return (
    <Loader2 className={cn('animate-spin text-primary-500', spinnerSizes[size], className)} />
  );
}

// Full page loading
export function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Spinner size="lg" />
    </div>
  );
}

// Skeleton loader
interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className }: SkeletonProps) {
  return <div className={cn('skeleton', className)} />;
}

// Skeleton card
export function SkeletonCard() {
  return (
    <div className="card p-4 space-y-3">
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
      <Skeleton className="h-3 w-full" />
    </div>
  );
}

// Empty state
interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}

export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      {icon || (
        <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-4">
          <Inbox className="w-8 h-8 text-slate-500" />
        </div>
      )}
      <h3 className="text-lg font-medium text-white mt-4">{title}</h3>
      {description && (
        <p className="text-slate-400 mt-2 max-w-sm">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// Error state
interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
}

export function ErrorState({
  title = 'حدث خطأ',
  message = 'حدث خطأ أثناء تحميل البيانات',
  onRetry
}: ErrorStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-16 h-16 bg-red-900/50 rounded-full flex items-center justify-center mb-4">
        <span className="text-2xl"></span>
      </div>
      <h3 className="text-lg font-medium text-white">{title}</h3>
      <p className="text-slate-400 mt-2">{message}</p>
      {onRetry && (
        <button onClick={onRetry} className="btn-primary mt-4">
          إعادة المحاولة
        </button>
      )}
    </div>
  );
}